import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import { Task } from "src/app/models/screens/task.model";
import ScheduleScreenViewModel from "src/app/screens/schedule/schedule.viewmodel";
import styles from "src/app/styles/screens/schedule.style";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PublicIcon from "@mui/icons-material/Public";
import { useNavigate } from "react-router-dom";
import { EventSummary } from "src/app/models/screens/events.model";

const ScheduleScreenView: React.FC = () => {
  const {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    userRole,
    children,
    isLoadingChildren,
    selectedOwnerId,
    onOwnerChanged,
    adminUsers,
    isLoadingAdminUsers,
    selectedDate,
    onDateChanged,
    tasks,
    isLoadingTasks,
    minutesToLabel,
    tags,
    events,
    isLoadingEvents,
  } = ScheduleScreenViewModel();
  const navigate = useNavigate();

  const getTaskBorderStyle = (task: Task) => {
    if (task.status === "Incomplete") return styles.taskTimelineItemOverdue;
    if (task.status === "Completed" || task.status === "CompletedOverdue")
      return styles.taskTimelineItemCompleted;
    return {};
  };

  const getStatusChipColor = (
    task: Task,
  ): "warning" | "success" | "error" | "default" => {
    if (task.status === "Completed") return "success";
    if (task.status === "CompletedOverdue") return "warning";
    if (task.status === "Incomplete") return "error";
    return "default";
  };

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Schedule" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Schedule
            </Typography>
          </Grid>

          {userRole === "Parent" && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="schedule-owner-select-label">
                      Viewing Schedule Of
                    </InputLabel>
                    <Select
                      labelId="schedule-owner-select-label"
                      value={selectedOwnerId}
                      label="Viewing Schedule Of"
                      onChange={(e: SelectChangeEvent) =>
                        onOwnerChanged(e.target.value)
                      }
                      disabled={isLoadingChildren}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {children.map((child) => (
                        <MenuItem key={child._id} value={child._id}>
                          {child.firstName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          {userRole === "Admin" && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id="admin-schedule-owner-label">
                      Viewing Schedule Of
                    </InputLabel>
                    <Select
                      labelId="admin-schedule-owner-label"
                      value={selectedOwnerId}
                      label="Viewing Schedule Of"
                      onChange={(e: SelectChangeEvent) =>
                        onOwnerChanged(e.target.value)
                      }
                      disabled={isLoadingAdminUsers}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {adminUsers.map((user: RequestManagementUser) => (
                        <MenuItem key={user.id} value={user.id}>
                          {`${user.firstName} ${user.lastName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6, xl: 4 }}>
                <Paper elevation={0} sx={styles.calendarContainer}>
                  <Typography sx={styles.calendarTitleText}>
                    Pick a Date
                  </Typography>
                  <DateCalendar value={selectedDate} onChange={onDateChanged} />
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6, xl: 8 }}>
                <Paper elevation={0} sx={styles.dayTasksContainer}>
                  <Typography sx={styles.dayTitleText}>
                    {selectedDate
                      ? `Schedule for ${selectedDate.format("MMMM D, YYYY")}`
                      : "Schedule"}
                  </Typography>

                  {!selectedDate ? (
                    <Typography sx={styles.subTitleText}>
                      Select a date to view the schedule.
                    </Typography>
                  ) : isLoadingTasks || isLoadingEvents ? (
                    <Box sx={styles.searchingLoader}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : tasks.length === 0 && events.length === 0 ? (
                    <Typography sx={styles.noTasksText}>
                      Nothing scheduled for this date.
                    </Typography>
                  ) : (
                    <>
                      {events.length > 0 && (
                        <>
                          <Typography sx={styles.sectionLabel}>
                            Events
                          </Typography>
                          {events.map((event: EventSummary) => (
                            <Paper
                              key={event.id}
                              elevation={0}
                              sx={styles.eventTimelineItem}
                              onClick={() => navigate(`/events/${event.id}`)}
                            >
                              <Box sx={styles.taskTitleRow}>
                                <Typography sx={styles.taskTitleText}>
                                  {event.title}
                                </Typography>
                                <Chip
                                  size="small"
                                  icon={<CelebrationOutlinedIcon />}
                                  label="Event"
                                  color="primary"
                                  variant="outlined"
                                />
                              </Box>
                              <Box sx={styles.chipRow}>
                                <Chip
                                  size="small"
                                  icon={
                                    event.isPublic ? (
                                      <PublicIcon />
                                    ) : (
                                      <LockOutlinedIcon />
                                    )
                                  }
                                  label={event.isPublic ? "Public" : "Private"}
                                  variant="outlined"
                                />
                                {event.isPaid && (
                                  <Chip
                                    size="small"
                                    icon={<PaymentsOutlinedIcon />}
                                    label={
                                      event.currency === "GBP"
                                        ? `£${event.participationFee.toFixed(2)}`
                                        : `${event.participationFee.toFixed(2)} ${event.currency}`
                                    }
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Typography sx={styles.taskMetaText}>
                                {`${minutesToLabel(event.from)} – ${minutesToLabel(event.to)}`}
                              </Typography>
                              {event.location && (
                                <Typography sx={styles.taskMetaText}>
                                  {event.location}
                                </Typography>
                              )}
                              {event.description && (
                                <>
                                  <Divider sx={{ my: 0.75 }} />
                                  <Typography
                                    sx={[styles.taskMetaText, { opacity: 0.7 }]}
                                  >
                                    {event.description}
                                  </Typography>
                                </>
                              )}
                            </Paper>
                          ))}
                        </>
                      )}

                      {tasks.length > 0 && (
                        <>
                          {events.length > 0 && (
                            <Typography sx={styles.sectionLabel}>
                              Tasks
                            </Typography>
                          )}
                          {tasks.map((task: Task) => (
                            <Paper
                              key={task.id}
                              elevation={0}
                              sx={[
                                styles.taskTimelineItem,
                                getTaskBorderStyle(task),
                              ]}
                            >
                              <Box sx={styles.taskTitleRow}>
                                <Typography sx={styles.taskTitleText}>
                                  {task.title}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={task.status}
                                  color={getStatusChipColor(task)}
                                />
                              </Box>
                              <Box sx={styles.chipRow}>
                                <Chip
                                  size="small"
                                  label={task.tag}
                                  variant="filled"
                                  sx={
                                    (task.tagColor ?? tags.get(task.tag))
                                      ? {
                                          backgroundColor:
                                            task.tagColor ?? tags.get(task.tag),
                                          color: "#fff",
                                        }
                                      : {}
                                  }
                                />
                                {task.isAssigned && (
                                  <Chip
                                    size="small"
                                    label={`Assigned by ${task.assignedByFullName}`}
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              <Typography sx={styles.taskMetaText}>
                                {`${minutesToLabel(task.from)} – ${minutesToLabel(task.to)}`}
                              </Typography>
                              <Divider sx={{ my: 0.75 }} />
                              <Typography
                                sx={[styles.taskMetaText, { opacity: 0.7 }]}
                              >
                                {task.description}
                              </Typography>
                            </Paper>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>

      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default ScheduleScreenView;
